import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Yoga Shastra";
const SITE_URL = "https://www.yoga-shastra.com";
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;
const CANONICAL = `${SITE_URL}/#yoga-goals`;

const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

export const YogGoalsSeoMeta = ({ goals = [], activeGoal = null }) => {
  const pageLabel = activeGoal?.title
    ? `${activeGoal.title} Yoga`
    : "Find Your Yoga Practice";

  const title = `${pageLabel} | ${SITE_NAME}`;
  const description = trimDesc(
    activeGoal?.description ||
      "Find a yoga practice that fits your goals, from stress relief and strength to women's wellness, prenatal yoga, back and neck care, and beginner yoga."
  );

  const keywords = [
    "yoga goals",
    "yoga for health",
    "yoga practice",
    ...goals.flatMap((goal) => [goal.title, ...(goal.keywords || [])]),
    activeGoal?.title,
  ]
    .filter(Boolean)
    .join(", ");

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Find Your Yoga Practice", item: CANONICAL },
      ...(activeGoal
        ? [{ "@type": "ListItem", position: 3, name: activeGoal.title, item: CANONICAL }]
        : []),
    ],
  };

  const itemListSchema = goals.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Yoga Goals",
        description: "Yoga practices organized by common health and wellness goals.",
        numberOfItems: goals.length,
        itemListElement: goals.map((goal, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: goal.title,
          description: goal.description,
          url: CANONICAL,
        })),
      }
    : null;

  return (
    <Helmet key={title}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={CANONICAL} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={CANONICAL} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1280" />
      <meta property="og:image:height" content="720" />
      <meta property="og:locale" content="en_IN" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@yogashastra" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={OG_IMAGE} />

      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      {itemListSchema && (
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      )}
    </Helmet>
  );
};

export default YogGoalsSeoMeta;
